import React, { useState } from 'react';
import { userService } from '../services/userService';
import { buildShareUrl } from '../utils/shareLinks';

const toDatetimeLocalValue = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const addDays = (days) => {
  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + days);
  return toDatetimeLocalValue(nextDate);
};

const getAvailabilityMode = (link) => {
  if (!link.endsAt) return 'none';
  if (link.availabilityMode) return link.availabilityMode;
  return 'custom';
};

const linkRoleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
];

const collaboratorRoleFor = (link, userId) => {
  if ((link.adminIds || []).includes(userId)) return 'admin';
  if ((link.managerIds || []).includes(userId)) return 'manager';
  return '';
};

const collaboratorName = (profile) => profile.displayName || profile.email || 'Signed-in user';

function AccessPanel({ isFreePlan = false, link, onChange }) {
  const shareUrl = buildShareUrl(link);
  const isPublicLink = link.protection === 'Public link';
  const inviteRequestMode = link.inviteRequestMode || 'request';
  const availabilityMode = getAvailabilityMode(link);
  const [collaboratorEmailQuery, setCollaboratorEmailQuery] = useState('');
  const [collaboratorRole, setCollaboratorRole] = useState('manager');
  const [collaboratorResults, setCollaboratorResults] = useState([]);
  const [collaboratorSearchStatus, setCollaboratorSearchStatus] = useState('');
  const [collaboratorSearchError, setCollaboratorSearchError] = useState('');

  const handleProtectionChange = (event) => {
    const protection = event.target.value;
    onChange({
      ...link,
      protection,
      ...(protection === 'Public link' ? { allowDownloads: false, allowSharing: true } : {}),
    });
  };

  const updateAvailability = (mode) => {
    if (mode === '7-days') {
      onChange({ ...link, availabilityMode: mode, startsAt: '', endsAt: addDays(7) });
      return;
    }

    if (mode === '14-days') {
      onChange({ ...link, availabilityMode: mode, startsAt: '', endsAt: addDays(14) });
      return;
    }

    if (mode === 'none') {
      onChange({ ...link, availabilityMode: mode, startsAt: '', endsAt: '' });
      return;
    }

    onChange({ ...link, availabilityMode: 'custom' });
  };

  const handleCollaboratorSearch = async (event) => {
    event.preventDefault();
    const emailQuery = collaboratorEmailQuery.trim();

    setCollaboratorSearchStatus('');
    setCollaboratorSearchError('');
    setCollaboratorResults([]);

    if (emailQuery.length < 3) {
      setCollaboratorSearchError('Enter at least 3 characters of the user email.');
      return;
    }

    try {
      setCollaboratorSearchStatus('Searching...');
      const results = await userService.searchUsersByEmail(emailQuery);
      const nextResults = results.filter((profile) => profile.uid !== link.ownerId && profile.id !== link.ownerId);
      setCollaboratorResults(nextResults);
      setCollaboratorSearchStatus(nextResults.length ? '' : 'No matching signed-in users found.');
    } catch (error) {
      setCollaboratorSearchError(error.message || 'Unable to search users.');
      setCollaboratorSearchStatus('');
    }
  };

  const applyCollaboratorRole = (profile, role = collaboratorRole) => {
    const userId = profile.uid || profile.id;
    if (!userId || userId === link.ownerId) return;

    const nextAdminIds = new Set(link.adminIds || []);
    const nextManagerIds = new Set(link.managerIds || []);
    nextAdminIds.delete(userId);
    nextManagerIds.delete(userId);

    if (role === 'admin') {
      nextAdminIds.add(userId);
    } else if (role === 'manager') {
      nextManagerIds.add(userId);
    }

    const existingCollaborators = Array.isArray(link.collaborators) ? link.collaborators : [];
    const nextCollaborator = {
      uid: userId,
      email: profile.email || '',
      displayName: profile.displayName || '',
      role,
    };

    onChange({
      ...link,
      adminIds: [...nextAdminIds],
      managerIds: [...nextManagerIds],
      collaborators: [
        nextCollaborator,
        ...existingCollaborators.filter((collaborator) => collaborator.uid !== userId),
      ],
    });
    setCollaboratorSearchStatus(`${collaboratorName(profile)} added as ${role}. Save link to persist access.`);
  };

  const removeCollaborator = (userId) => {
    onChange({
      ...link,
      adminIds: (link.adminIds || []).filter((id) => id !== userId),
      managerIds: (link.managerIds || []).filter((id) => id !== userId),
      collaborators: (link.collaborators || []).filter((collaborator) => collaborator.uid !== userId),
    });
    setCollaboratorSearchStatus('Collaborator removed. Save link to persist access.');
  };

  const assignedCollaborators = [
    ...(link.collaborators || []),
    ...(link.adminIds || [])
      .filter((uid) => !(link.collaborators || []).some((collaborator) => collaborator.uid === uid))
      .map((uid) => ({ uid, displayName: '', email: uid, role: 'admin' })),
    ...(link.managerIds || [])
      .filter((uid) => !(link.collaborators || []).some((collaborator) => collaborator.uid === uid))
      .map((uid) => ({ uid, displayName: '', email: uid, role: 'manager' })),
  ].filter((collaborator, index, collaborators) => (
    collaborator.uid &&
    collaborators.findIndex((item) => item.uid === collaborator.uid) === index
  ));

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5">
      <p className="label-caps mb-4">Access control</p>
      <div className="grid gap-3">
        <div>
          <label className="field-label mt-0" htmlFor="protection">Protection</label>
          <select
            id="protection"
            className="field"
            value={link.protection}
            onChange={handleProtectionChange}
          >
            <option>Private</option>
            <option>Invite only</option>
            <option>Password</option>
            <option>Public link</option>
          </select>
          {link.protection === 'Private' && (
            <p className="mt-2 text-xs leading-5 text-slate-500">
              Private links only open for the signed-in owner.
            </p>
          )}
        </div>

        <div>
          <p className="field-label">Availability</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              { id: '7-days', label: '7 days' },
              { id: '14-days', label: '14 days' },
              { id: 'custom', label: 'Start Date to End Date' },
              { id: 'none', label: 'No Expiry' },
            ].map((option) => (
              <button
                aria-pressed={availabilityMode === option.id}
                className={availabilityMode === option.id ? 'segmented active' : 'segmented'}
                disabled={isFreePlan && option.id === 'none'}
                key={option.id}
                onClick={() => updateAvailability(option.id)}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
          {isFreePlan && (
            <p className="mt-2 text-xs leading-5 text-slate-500">
              Free links expire 14 days after creation.
            </p>
          )}
        </div>

        {availabilityMode === 'custom' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label" htmlFor="starts-at">Start</label>
              <input
                id="starts-at"
                className="field"
                type="datetime-local"
                value={link.startsAt || ''}
                onChange={(event) => onChange({ ...link, availabilityMode: 'custom', startsAt: event.target.value })}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="ends-at">End</label>
              <input
                id="ends-at"
                className="field"
                disabled={isFreePlan}
                type="datetime-local"
                value={link.endsAt || ''}
                onChange={(event) => onChange({ ...link, availabilityMode: 'custom', endsAt: event.target.value })}
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs text-slate-500">Public share URL</p>
        <p className="mt-1 break-all text-sm font-semibold text-slate-950">{shareUrl}</p>
      </div>

      {link.protection === 'Invite only' && (
        <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase text-slate-500">Invite entry mode</p>
          <div className="mt-3 grid grid-cols-2 gap-2 rounded border border-slate-200 bg-white p-1">
            <button
              aria-pressed={inviteRequestMode === 'request'}
              className={inviteRequestMode === 'request' ? 'segmented active' : 'segmented'}
              onClick={() => onChange({ ...link, inviteRequestMode: 'request' })}
              type="button"
            >
              Request invite
            </button>
            <button
              aria-pressed={inviteRequestMode === 'waiting-list'}
              className={inviteRequestMode === 'waiting-list' ? 'segmented active' : 'segmented'}
              onClick={() => onChange({ ...link, inviteRequestMode: 'waiting-list' })}
              type="button"
            >
              Waiting list
            </button>
          </div>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {inviteRequestMode === 'waiting-list'
              ? 'Visitors can join a waiting list with contact details and a short note before you approve access.'
              : 'Visitors can request an invite with an email address, mobile number, and a short reason before they receive access credentials.'}
          </p>
        </div>
      )}

      <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold uppercase text-slate-500">Recipient permissions</p>
        <div className="mt-3 grid gap-3">
          <label className="flex items-start gap-3 text-sm font-semibold text-slate-700">
            <input
              checked={Boolean(link.demoMode)}
              onChange={(event) => onChange({ ...link, demoMode: event.target.checked })}
              type="checkbox"
            />
            <span>
              Demo mode
              <span className="block text-xs font-normal leading-5 text-slate-500">
                Visitors with access to this link can try internal forms, but submissions are disabled.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm font-semibold text-slate-700">
            <input
              checked={Boolean(link.allowDownloads)}
              onChange={(event) => onChange({ ...link, allowDownloads: event.target.checked })}
              type="checkbox"
            />
            <span>Allow downloads</span>
          </label>
          <label className="flex items-start gap-3 text-sm font-semibold text-slate-700">
            <input
              checked={isPublicLink || Boolean(link.allowSharing)}
              disabled={isPublicLink}
              onChange={(event) => onChange({ ...link, allowSharing: event.target.checked })}
              type="checkbox"
            />
            <span>
              Allow sharing
              {isPublicLink && <span className="block text-xs font-normal text-slate-500">Public links allow sharing by default.</span>}
            </span>
          </label>
        </div>
      </div>

      <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold uppercase text-slate-500">Signed-in collaborators</p>
        <form className="mt-3 grid gap-3" onSubmit={handleCollaboratorSearch}>
          <div>
            <label className="field-label mt-0" htmlFor="collaborator-email-search">Search by email</label>
            <input
              className="field"
              id="collaborator-email-search"
              onChange={(event) => setCollaboratorEmailQuery(event.target.value)}
              placeholder="teammate@example.com"
              type="email"
              value={collaboratorEmailQuery}
            />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <select
              aria-label="Collaborator link role"
              className="field"
              onChange={(event) => setCollaboratorRole(event.target.value)}
              value={collaboratorRole}
            >
              {linkRoleOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              className="rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              type="submit"
            >
              Search
            </button>
          </div>
        </form>

        {collaboratorSearchError && (
          <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{collaboratorSearchError}</p>
        )}
        {collaboratorSearchStatus && (
          <p className="mt-3 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">{collaboratorSearchStatus}</p>
        )}

        {collaboratorResults.length > 0 && (
          <div className="mt-3 grid gap-2">
            {collaboratorResults.map((profile) => {
              const userId = profile.uid || profile.id;
              const assignedRole = collaboratorRoleFor(link, userId);

              return (
                <div className="rounded border border-slate-200 bg-white p-3" key={userId}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-950">{collaboratorName(profile)}</p>
                      <p className="mt-1 truncate text-xs text-slate-500">{profile.email}</p>
                    </div>
                    {assignedRole && <span className="chip">{assignedRole}</span>}
                  </div>
                  <button
                    className="mt-3 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                    onClick={() => applyCollaboratorRole(profile)}
                    type="button"
                  >
                    {assignedRole ? `Set as ${collaboratorRole}` : `Add as ${collaboratorRole}`}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {assignedCollaborators.length > 0 && (
          <div className="mt-4 grid gap-2 border-t border-slate-200 pt-3">
            {assignedCollaborators.map((collaborator) => (
              <div className="flex items-center justify-between gap-3 rounded border border-slate-200 bg-white p-3" key={collaborator.uid}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-950">
                    {collaborator.displayName || collaborator.email || collaborator.uid}
                  </p>
                  <p className="mt-1 text-xs font-semibold uppercase text-slate-500">
                    {collaboratorRoleFor(link, collaborator.uid) || collaborator.role}
                  </p>
                </div>
                <button
                  className="text-sm font-semibold text-red-700"
                  onClick={() => removeCollaborator(collaborator.uid)}
                  type="button"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default AccessPanel;
